/**
 * DATE UTILS - NeoApp
 */
import { WEEKDAY_OPTIONS } from "../constants/calendar";;

export const formatDate = (date) => {
    if (!date) return "";

    // Tratamento seguro para string de calendário "YYYY-MM-DD", evita bugs de timezone
    if (typeof date === 'string' && date.includes('-') && date.length <= 10) {
        const parts = date.split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }

    // Suporte a Firestore Timestamps
    if (typeof date?.toDate === 'function') date = date.toDate();
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("pt-BR");
};

export const formatDateTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' })}`;
};

export const parseDateInput = (dateString) => {
    if (!dateString) return new Date();
    // Se a string contiver "-", como "2024-10-28", ignoramos o timezone ao instanciar
    if (typeof dateString === 'string' && dateString.includes('-')) {
        const parts = dateString.split('-').map(Number);
        if (parts.length === 3) {
            return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
        }
    }
    return new Date(dateString);
};

export const toISODate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

export const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    return date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();
};

export const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const getStartOfWeek = (date) => {
    const result = new Date(date);
    const day = result.getDay();
    // Se domingo (0), queremos que a semana comece no domingo mesmo
    const diff = result.getDate() - day;
    return new Date(result.setDate(diff));
};

export const normalizeDate = (date) => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
};

export const minutesToTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

export const formatDayHeaderLabel = (date) => {
    const d = new Date(date);
    const dayLabel = WEEKDAY_OPTIONS[d.getDay()]?.label || "";
    return `${dayLabel} ${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
};

export const getStepForView = (view) => {
    return view === "day" ? 1 : 7;
};

export const formatRangeLabel = (start, end, view) => {
    const s = new Date(start);
    const e = new Date(end);
    if (view === "day") {
        return s.toLocaleDateString("pt-BR", { day: "numeric", month: "long" });
    }
    return `${s.getDate().toString().padStart(2, "0")}/${(s.getMonth() + 1).toString().padStart(2, "0")} - ${e.getDate().toString().padStart(2, "0")}/${(e.getMonth() + 1).toString().padStart(2, "0")}`;
};

export const timeToMinutes = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
};

export const parseFirestoreDate = (date) => {
    if (!date) return null;
    if (date.toDate) return date.toDate();
    if (date.seconds) return new Date(date.seconds * 1000);
    return new Date(date);
};

export const format = (date, pattern) => {
    // Simple format implementation
    const d = new Date(date);
    if (pattern === 'yyyy-MM-dd' || pattern === 'YYYY-MM-DD') {
        return toISODate(d);
    }
    return d.toLocaleDateString('pt-BR');
};

/**
 * Calcula a idade com base na data de nascimento.
 * @param {string} birthDate - Data de nascimento no formato YYYY-MM-DD
 * @returns {number|null} Idade em anos ou null se inválido
 */
export const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate + "T00:00:00");
    if (isNaN(birth.getTime())) return null;
    if (birth.getFullYear() < 1900) return null; // Ignora datas de fallback/zeradas como 0001-01-01
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
};

/**
 * Retorna a data atual formatada como "YYYY-MM-DD" no fuso oficial (America/Sao_Paulo).
 * @returns {string} Data atual no formato YYYY-MM-DD
 */
export const getTodayInTenantTimezone = () => {
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
};

/**
 * Verifica se uma data (no formato YYYY-MM-DD ou ISO) está no futuro em relação ao fuso de São Paulo.
 * @param {string} dateStr - Data a ser verificada
 * @returns {boolean}
 */
export const isFutureDate = (dateStr) => {
    if (!dateStr) return false;
    const datePart = dateStr.split('T')[0];
    const todayStr = getTodayInTenantTimezone();
    return datePart > todayStr;
};

/**
 * Formata a data de uma tarefa de modo seguro contra timezone.
 * Ex: "2026-06-13" e "20:00" -> "13/06 às 20:00"
 * @param {string} scheduledDate - Data agendada YYYY-MM-DD
 * @param {string} [scheduledTime] - Hora agendada HH:mm
 * @returns {string}
 */
export const formatTaskDate = (scheduledDate, scheduledTime) => {
    if (!scheduledDate) return "";
    const datePart = scheduledDate.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length === 3) {
        const [, month, day] = parts;
        const timeStr = scheduledTime ? ` às ${scheduledTime}` : '';
        return `${day}/${month}${timeStr}`;
    }
    return scheduledDate;
};

/**
 * Calcula a diferença em dias de uma data YYYY-MM-DD em relação ao dia de hoje (fuso de São Paulo).
 * Retorna número negativo para atrasado, 0 para hoje, e positivo para futuro.
 * @param {string} rawDate
 * @returns {number|null}
 */
export const getDaysDifferenceFromToday = (rawDate) => {
    if (!rawDate) return null;
    try {
        const datePart = rawDate.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        const target = new Date(year, month - 1, day, 0, 0, 0, 0);
        
        const todayStr = getTodayInTenantTimezone();
        const [tYear, tMonth, tDay] = todayStr.split('-').map(Number);
        const today = new Date(tYear, tMonth - 1, tDay, 0, 0, 0, 0);

        const diffTime = target.getTime() - today.getTime();
        return Math.round(diffTime / 86400000);
    } catch {
        return null;
    }
};

